/**
 * utils/deviceDetection.ts
 * Device identification and browser/OS detection utilities
 *
 * Provides stable device ID generation and user-friendly device naming
 * for multi-device sync tracking without external dependencies.
 */

import { generateUUID } from './uuid';
import { Brand } from '@enfomentis/shared/config';

/**
 * localStorage key for device ID persistence
 */
const DEVICE_ID_STORAGE_KEY = `${Brand.cachePrefix}-device-id`;

/**
 * Browser information interface
 */
export interface BrowserInfo {
  name: string;
  version: string;
}

/**
 * Operating system information interface
 */
export interface OSInfo {
  name: string;
  version?: string;
}

/**
 * Get or create a stable device ID
 *
 * Returns existing device ID from localStorage if available,
 * otherwise generates a new UUID and stores it.
 *
 * @returns Stable device ID (UUID v4 format)
 */
export function getOrCreateDeviceId(): string {
  try {
    // Check for existing device ID
    const existingId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existingId) {
      return existingId;
    }

    // Generate new device ID
    const newDeviceId = generateUUID();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, newDeviceId);

    console.log('🆔 Generated new device ID:', newDeviceId);
    return newDeviceId;
  } catch (error) {
    console.error('❌ Error accessing localStorage for device ID:', error);
    // Fallback: Generate UUID without persistence (will change on reload)
    return generateUUID();
  }
}

/**
 * Get browser information from user agent
 *
 * Detects browser name and version from navigator.userAgent
 *
 * @returns Browser information object
 */
export function getBrowserInfo(): BrowserInfo {
  const userAgent = navigator.userAgent;

  // Edge (Chromium-based) - must check before Chrome
  if (userAgent.includes('Edg/')) {
    const match = userAgent.match(/Edg\/([\d.]+)/);
    return {
      name: 'Edge',
      version: match && match[1] ? match[1] : 'Unknown'
    };
  }

  // Chrome
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    const match = userAgent.match(/Chrome\/([\d.]+)/);
    return {
      name: 'Chrome',
      version: match && match[1] ? match[1] : 'Unknown'
    };
  }

  // Safari - must check after Chrome (Chrome includes Safari in UA)
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    const match = userAgent.match(/Version\/([\d.]+)/);
    return {
      name: 'Safari',
      version: match && match[1] ? match[1] : 'Unknown'
    };
  }

  // Firefox
  if (userAgent.includes('Firefox/')) {
    const match = userAgent.match(/Firefox\/([\d.]+)/);
    return {
      name: 'Firefox',
      version: match && match[1] ? match[1] : 'Unknown'
    };
  }

  // Opera
  if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) {
    const match = userAgent.match(/(?:OPR|Opera)\/([\d.]+)/);
    return {
      name: 'Opera',
      version: match && match[1] ? match[1] : 'Unknown'
    };
  }

  // Fallback
  return {
    name: 'Unknown Browser',
    version: 'Unknown'
  };
}

/**
 * Get operating system information from user agent
 *
 * Detects OS name and version from navigator.userAgent and navigator.platform
 *
 * @returns Operating system information object
 */
export function getOSInfo(): OSInfo {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // Windows
  if (userAgent.includes('Windows NT')) {
    const match = userAgent.match(/Windows NT ([\d.]+)/);
    const version = match ? match[1] : null;

    // Map Windows NT versions to marketing names
    const versionMap: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
      '6.0': 'Vista',
      '5.1': 'XP'
    };

    if (version) {
      return {
        name: 'Windows',
        version: versionMap[version] || version
      };
    } else {
      return {
        name: 'Windows'
      };
    }
  }

  // macOS
  if (userAgent.includes('Mac OS X') || platform.includes('Mac')) {
    const match = userAgent.match(/Mac OS X ([\d_]+)/);
    if (match && match[1]) {
      return {
        name: 'MacOS',
        version: match[1].replace(/_/g, '.')
      };
    } else {
      return {
        name: 'MacOS'
      };
    }
  }

  // iOS
  if (userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod')) {
    const match = userAgent.match(/OS ([\d_]+)/);
    if (match && match[1]) {
      return {
        name: 'iOS',
        version: match[1].replace(/_/g, '.')
      };
    } else {
      return {
        name: 'iOS'
      };
    }
  }

  // Android
  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android ([\d.]+)/);
    if (match && match[1]) {
      return {
        name: 'Android',
        version: match[1]
      };
    } else {
      return {
        name: 'Android'
      };
    }
  }

  // Linux
  if (userAgent.includes('Linux') || platform.includes('Linux')) {
    return {
      name: 'Linux'
    };
  }

  // Fallback
  return {
    name: 'Unknown OS'
  };
}

/**
 * Generate user-friendly device name
 *
 * Creates a readable device name in the format "{Browser} ({UUID}) on {OS}"
 * Examples: "Chrome (a3f2c) on MacOS", "Edge (b7d9e) on Windows 10"
 *
 * The UUID suffix ensures uniqueness across different browser profiles
 * on the same machine.
 *
 * @returns Formatted device name string
 */
export function generateDeviceName(): string {
  try {
    const browser = getBrowserInfo();
    const os = getOSInfo();
    const deviceId = getOrCreateDeviceId();

    // Extract first 5 characters of device ID for uniqueness
    const shortId = deviceId.substring(0, 5);

    // Format: "Browser (UUID) on OS" or "Browser (UUID) on OS Version"
    const osName = os.version ? `${os.name} ${os.version}` : os.name;
    return `${browser.name} (${shortId}) on ${osName}`;
  } catch (error) {
    console.error('❌ Error generating device name:', error);
    return 'Unknown Browser on Unknown OS';
  }
}
